import { useState } from "react";

import { appApi } from "../../core/api/app.js";
import { addDays, dayLabelFull, dayTick, upTo, weekLabel, weekRange, weekStart } from "../../core/format/periods.js";
import { useAppSession } from "../../state/AppSessionContext.jsx";
import { useEndpoint } from "../../state/useEndpoint.js";
import { Columns, Empty, Failed, Loading, Panel } from "../components/Panel.jsx";
import PeriodNav from "../components/PeriodNav.jsx";

export default function Food() {
  const { token, me } = useAppSession();
  const today = me?.today ?? new Date().toISOString().slice(0, 10);
  const currentWeek = weekStart(today);
  const [start, setStart] = useState(currentWeek);

  const { from, to } = weekRange(start);
  const { data, loading, error } = useEndpoint(() => appApi.diet(token, from, to), [token, from, to]);

  const byDate = new Map((data?.days ?? []).map((d) => [d.date, d]));
  const week = upTo(Array.from({ length: 7 }, (_, i) => addDays(start, i)), today);
  const logged = data?.days ?? [];

  return (
    <>
      <PeriodNav
        label={weekLabel(start)}
        onPrev={() => setStart(addDays(start, -7))}
        onNext={() => setStart(addDays(start, 7))}
        atLatest={start >= currentWeek}
        right={data ? `${logged.length} of 7 days logged` : null}
      />

      {loading && <Loading />}
      {error && <Failed error={error} />}

      {data && !loading && (
        <>
          <Panel title="Calories through the week">
            {logged.length === 0 ? (
              <Empty>Nothing logged this week.</Empty>
            ) : (
              <Columns
                rows={week.map((date) => ({
                  label: dayTick(date).split(" ")[0],
                  tooltip: dayLabelFull(date, today),
                  value: byDate.get(date)?.totals?.caloriesConsumed ?? 0,
                }))}
                format={(v) => `${v} kcal`}
              />
            )}
          </Panel>

          {week.map((date) => {
            const day = byDate.get(date);
            return (
              <Panel
                key={date}
                title={dayLabelFull(date, today)}
                meta={
                  day?.totals
                    ? `${day.totals.caloriesConsumed} kcal · P ${day.totals.protein} · C ${day.totals.carbs} · F ${day.totals.fat}`
                    : date
                }
              >
                {!day ? (
                  <Empty>Nothing logged.</Empty>
                ) : (
                  <div className="meals">
                    {day.meals.map((meal, i) => (
                      <div className="meal" key={i}>
                        <div className="meal-head">{meal.mealType ?? "Meal"}</div>
                        <ul className="mini-list">
                          {meal.items.map((item, j) => (
                            <li key={j}>
                              <span>
                                {item.name}
                                {item.quantity ? ` · ${item.quantity}` : ""}
                              </span>
                              <span>{item.calories != null ? `${item.calories} kcal` : ""}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                    {day.waterIntakeMl != null && (
                      <div className="meal">
                        <div className="meal-head">Water</div>
                        <div className="mini-big">{day.waterIntakeMl} ml</div>
                      </div>
                    )}
                  </div>
                )}
              </Panel>
            );
          })}
        </>
      )}
    </>
  );
}
